import React, { useState, useMemo } from 'react';
import IconButton from "@mui/material/IconButton";
import EditIcon from "@mui/icons-material/Edit";
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import '../../../componentsStyling/EditCheckIn.css';
import { addSeconds, formatToDisplayTime } from "../../../utils/dateTimeUtils";
import { useReservationApis } from '../../../apis/ReservationAPIs';

function EditCheckIn({ data, updateCheckIn }) {
  const { fetchMaxExtensionTime } = useReservationApis();
  const [open, setOpen] = useState(false);
  // Format time from HH:MM:SS to HH:MM for input
  const formatTimeForInput = (time) => {
    if (!time) return '';
    return time.includes(':') ? time.substring(0, 5) : time;
  };
  const [checkInDate, setCheckInDate] = useState(data.checkInDate || '');
  const [checkInTime, setCheckInTime] = useState(formatTimeForInput(data.checkInTime) || '');
  const [maxExtensionInfo, setMaxExtensionInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [extensionMessage, setExtensionMessage] = useState('');
  
  // Operating hours from backend (will be set when extension info is loaded)
  const openingTimeHour = maxExtensionInfo?.openingTimeHour ?? 8;
  const openingTimeMinute = maxExtensionInfo?.openingTimeMinute ?? 0;
  const closingTimeHour = maxExtensionInfo?.closingTimeHour ?? 18; // 6:00 PM
  const closingTimeMinute = maxExtensionInfo?.closingTimeMinute ?? 0;

  const handleClickOpen = async (event) => {
    event.stopPropagation(); // Prevent row click
    setOpen(true);
    setLoading(true);
    setError(null);
    setExtensionMessage('');
    
    // Reset to current values
    setCheckInDate(data.checkInDate || '');
    setCheckInTime(formatTimeForInput(data.checkInTime) || '');
    
    // Fetch max extension info
    const result = await fetchMaxExtensionTime(data.reservationId);
    setLoading(false);
    
    if (result.success && result.data) {
      setMaxExtensionInfo(result.data);
      const hours = result.data.maxExtensionHours || 0;
      const minutes = result.data.maxExtensionMinutes || 0;
      
      if (hours > 0 || minutes > 0) {
        let message = 'You can extend by up to ';
        if (hours > 0) {
          message += `${hours} hour${hours !== 1 ? 's' : ''}`;
        }
        if (minutes > 0) {
          if (hours > 0) message += ' and ';
          message += `${minutes} minute${minutes !== 1 ? 's' : ''}`;
        }
        message += '.';
        setExtensionMessage(message);
      } else {
        setExtensionMessage('No extension available.');
      }
    } else {
      setError(result.error || 'Unable to fetch extension information.');
      setExtensionMessage('Extension may not be available.');
    }
  };

  const handleClose = () => {
    setOpen(false);
    setMaxExtensionInfo(null);
    setError(null);
    setExtensionMessage('');
  };

  const handleEditCheckIn = () => {
    const extractedData = {
      assetTag: data.assetTag,
      userId: data.userId,
      reservationDate: data.reservationDate,
      checkOut: data.checkOutDate,
      checkOutTime: data.checkOutTime,
      checkIn: checkInDate,
      checkInTime: checkInTime,
      active: data.active,
    };

    const updatedReservation = {
      ...extractedData,
      checkInTime: addSeconds(checkInTime),
    };

    updateCheckIn(updatedReservation, data.reservationId);
    handleClose();
  };

  // Calculate min/max dates and times for pickers
  const { minDate, maxDate, minTime, maxTime } = useMemo(() => {
    if (!maxExtensionInfo || !data.checkInDate) {
      return { minDate: null, maxDate: null, minTime: null, maxTime: null };
    }

    const currentDate = dayjs(data.checkInDate);
    const maxDateValue = dayjs(maxExtensionInfo.maxCheckInDate);
    
    // Parse max time (format: HH:mm:ss)
    const maxTimeStr = maxExtensionInfo.maxCheckInTime || '22:00:00';
    const [maxHour, maxMin] = maxTimeStr.split(':').map(Number);
    const maxTimeValue = dayjs().hour(maxHour).minute(maxMin).second(0);

    return {
      minDate: currentDate,
      maxDate: maxDateValue,
      minTime: dayjs().hour(openingTimeHour).minute(openingTimeMinute).second(0),
      maxTime: maxTimeValue
    };
  }, [maxExtensionInfo, data.checkInDate, openingTimeHour, openingTimeMinute]);

  // Calculate time constraints based on selected date
  const timeConstraints = useMemo(() => {
    if (!maxExtensionInfo || !checkInDate) {
      return {
        minTime: dayjs().hour(openingTimeHour).minute(openingTimeMinute).second(0),
        maxTime: dayjs().hour(closingTimeHour).minute(closingTimeMinute).second(0) // 6:00 PM
      };
    }

    const selectedDate = dayjs(checkInDate);
    const maxDateValue = dayjs(maxExtensionInfo.maxCheckInDate);
    const maxTimeStr = maxExtensionInfo.maxCheckInTime || `${closingTimeHour.toString().padStart(2, '0')}:${closingTimeMinute.toString().padStart(2, '0')}:00`;
    const [maxHour, maxMin] = maxTimeStr.split(':').map(Number);
    
    // If selected date is the max date, use the max time, otherwise use closing time
    const isMaxDate = selectedDate.isSame(maxDateValue, 'day');
    const maxTimeValue = isMaxDate 
      ? dayjs().hour(maxHour).minute(maxMin).second(0)
      : dayjs().hour(closingTimeHour).minute(closingTimeMinute).second(0);

    // If selected date is the current date, min time should be after current check-in time
    const currentDate = dayjs(data.checkInDate);
    const currentTimeStr = data.checkInTime || `${openingTimeHour.toString().padStart(2, '0')}:${openingTimeMinute.toString().padStart(2, '0')}:00`;
    const [currentHour, currentMin] = currentTimeStr.split(':').map(Number);
    const currentTime = dayjs().hour(currentHour).minute(currentMin).second(0);
    
    const isCurrentDate = selectedDate.isSame(currentDate, 'day');
    const minTimeValue = isCurrentDate 
      ? currentTime.add(30, 'minute') // At least 30 minutes after current time
      : dayjs().hour(openingTimeHour).minute(openingTimeMinute).second(0);

    return {
      minTime: minTimeValue,
      maxTime: maxTimeValue
    };
  }, [checkInDate, maxExtensionInfo, data.checkInDate, data.checkInTime, openingTimeHour, openingTimeMinute, closingTimeHour, closingTimeMinute]);

  // Convert date string to dayjs object for date picker
  const checkInDateValue = checkInDate ? dayjs(checkInDate) : null;
  
  // Generate time options in 30-minute intervals based on constraints
  const timeOptions = useMemo(() => {
    if (!checkInDate || !timeConstraints.minTime || !timeConstraints.maxTime) {
      return [];
    }
    
    const options = [];
    // Clone to avoid mutating the original
    let currentTime = timeConstraints.minTime.clone();
    const maxTime = timeConstraints.maxTime;
    
    // Start from minTime and add 30-minute intervals until maxTime
    while (currentTime.isBefore(maxTime) || currentTime.isSame(maxTime, 'minute')) {
      // Format as HH:mm for storage
      const time24 = currentTime.format('HH:mm');
      // Format as display time (HH:MM AM/PM)
      const timeDisplay = formatToDisplayTime(`${time24}:00`);
      options.push({ value: time24, label: timeDisplay });
      currentTime = currentTime.add(30, 'minute');
    }
    
    return options;
  }, [checkInDate, timeConstraints]);

  return (
    <div>
      <IconButton onClick={handleClickOpen}>
        <EditIcon color="primary" />
      </IconButton>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Check-In</DialogTitle>
        <DialogContent>
          {loading && (
            <Box display="flex" justifyContent="center" alignItems="center" p={2}>
              <CircularProgress size={24} />
              <Typography variant="body2" sx={{ ml: 2 }}>Loading extension information...</Typography>
            </Box>
          )}
          
          {error && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          {extensionMessage && !loading && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {extensionMessage}
            </Alert>
          )}
          
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <div className="check-in-field">
              <DatePicker
                label="Check-In Date"
                value={checkInDateValue}
                onChange={(newValue) => {
                  if (newValue) {
                    const newDateStr = newValue.format('YYYY-MM-DD');
                    setCheckInDate(newDateStr);
                    
                    // Recalculate time constraints for the new date
                    if (maxExtensionInfo) {
                      const selectedDate = newValue;
                      const maxDateValue = dayjs(maxExtensionInfo.maxCheckInDate);
                      const maxTimeStr = maxExtensionInfo.maxCheckInTime || `${closingTimeHour.toString().padStart(2, '0')}:${closingTimeMinute.toString().padStart(2, '0')}:00`;
                      const [maxHour, maxMin] = maxTimeStr.split(':').map(Number);
                      
                      const isMaxDate = selectedDate.isSame(maxDateValue, 'day');
                      const maxTimeValue = isMaxDate 
                        ? dayjs().hour(maxHour).minute(maxMin).second(0)
                        : dayjs().hour(closingTimeHour).minute(closingTimeMinute).second(0);
                      
                      const currentDate = dayjs(data.checkInDate);
                      const currentTimeStr = data.checkInTime || `${openingTimeHour.toString().padStart(2, '0')}:${openingTimeMinute.toString().padStart(2, '0')}:00`;
                      const [currentHour, currentMin] = currentTimeStr.split(':').map(Number);
                      const currentTime = dayjs().hour(currentHour).minute(currentMin).second(0);
                      
                      const isCurrentDate = selectedDate.isSame(currentDate, 'day');
                      const minTimeValue = isCurrentDate 
                        ? currentTime.add(30, 'minute')
                        : dayjs().hour(openingTimeHour).minute(openingTimeMinute).second(0);
                      
                      // Reset time if current time is invalid for new date
                      if (checkInTime) {
                        const currentTimeValue = dayjs(checkInTime, 'HH:mm');
                        if (currentTimeValue.isBefore(minTimeValue) || currentTimeValue.isAfter(maxTimeValue)) {
                          setCheckInTime(minTimeValue.format('HH:mm'));
                        }
                      } else {
                        setCheckInTime(minTimeValue.format('HH:mm'));
                      }
                    }
                  }
                }}
                minDate={minDate}
                maxDate={maxDate}
                shouldDisableDate={(day) => {
                  // Disable weekends
                  const dayOfWeek = day.day();
                  if (dayOfWeek === 0 || dayOfWeek === 6) return true;
                  
                  // Disable dates before min or after max
                  if (minDate && day.isBefore(minDate, 'day')) return true;
                  if (maxDate && day.isAfter(maxDate, 'day')) return true;
                  
                  return false;
                }}
                disabled={loading}
                slotProps={{
                  textField: {
                    fullWidth: true
                  }
                }}
              />
            </div>
            <div className="check-in-field">
              <FormControl fullWidth disabled={loading || !checkInDate}>
                <InputLabel id="check-in-time-label">Check-In Time</InputLabel>
                <Select
                  labelId="check-in-time-label"
                  label="Check-In Time"
                  value={checkInTime || ''}
                  onChange={(e) => setCheckInTime(e.target.value)}
                  MenuProps={{ PaperProps: { style: { maxHeight: 300 } } }}
                >
                  {timeOptions.length === 0 ? (
                    <MenuItem value="" disabled>
                      {!checkInDate ? 'Please select a date first' : 'No available times'}
                    </MenuItem>
                  ) : (
                    timeOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))
                  )}
                </Select>
                {checkInDate && maxExtensionInfo && timeOptions.length > 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.5 }}>
                    Available: {timeOptions[0].label} - {timeOptions[timeOptions.length - 1].label}
                  </Typography>
                )}
              </FormControl>
            </div>
          </LocalizationProvider>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleEditCheckIn} disabled={loading}>Save</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default EditCheckIn;
